import type { ReactElement } from 'react';
import type { ChartConfig } from '@parrot-software/design-system';
import { formatCellValue } from './dataFormatters';
import type { ChartSeriesConfig } from '@reports/domain/value-objects/blocks';
import type { TransformedChartData } from './chartDataTransformers';
import { ReportDataFormats } from '@reports/domain/types';

/**
 * Pie chart label entry from Recharts
 */
export interface PieLabelEntry {
  cx: number;
  cy: number;
  midAngle: number;
  outerRadius: number;
  percent: number;
  name: string;
  value: number;
}

/**
 * Creates a label formatter for bar chart top labels
 *
 * Formats the value displayed on top of bar chart columns.
 *
 * @param {ChartSeriesConfig} seriesConfig - Series configuration with formatting rules
 * @returns {Function} Label formatter function
 *
 * @example
 * const formatter = createTopLabelFormatter(seriesConfig);
 * <LabelList dataKey="value" formatter={formatter} position="top" />
 */
export const createTopLabelFormatter = (seriesConfig: ChartSeriesConfig) => {
  return (value: number): string => {
    return formatCellValue(value.toString(), seriesConfig.format);
  };
};

/**
 * Creates a custom label renderer for pie chart segments
 *
 * Renders labels outside the pie chart with truncated names and percentages.
 * Labels are positioned dynamically based on the segment angle.
 *
 * @returns {Function} Label renderer function
 *
 * @example
 * const renderLabel = createPieLabelRenderer();
 * <Pie label={renderLabel} />
 */
export const createPieLabelRenderer = () => {
  return (entry: PieLabelEntry): ReactElement => {
    const { cx, cy, midAngle, outerRadius, percent } = entry;

    // Format percentage
    const formattedPercent = `${(percent * 100).toFixed(1)}%`;

    // Calculate label position outside the pie
    const RADIAN = Math.PI / 180;
    const radius = outerRadius + 20;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    const textAnchor = x > cx ? 'start' : 'end';

    return (
      <text
        className="text-xs font-medium"
        dominantBaseline="central"
        fill="var(--foreground)"
        textAnchor={textAnchor}
        x={x}
        y={y}
      >
        {` ${formattedPercent}`}
      </text>
    );
  };
};

/**
 * Creates a legend label for bar charts
 *
 * Generates the legend label with currency suffix when the series format is currency.
 * Appends " MXN" to the translated label for currency formats.
 *
 * @param {ChartSeriesConfig} seriesConfig - Series configuration with formatting rules
 * @param {Function} t - Translation function
 * @returns {string} Formatted legend label
 *
 * @example
 * const legendLabel = createBarLegendLabel(seriesConfig, t);
 * // Returns "Total Sales MXN" for currency format
 * // Returns "Total Orders" for non-currency format
 */
export const createBarLegendLabel = (seriesConfig: ChartSeriesConfig, t: (key: string) => string): string => {
  const labelSeries = t(seriesConfig.label);
  const isCurrency = seriesConfig.format.type === ReportDataFormats.CURRENCY;

  return isCurrency ? `${labelSeries} MXN` : labelSeries;
};

/**
 * Creates a chart configuration for bar charts
 *
 * Builds the ChartConfig object required by shadcn ChartContainer for bar charts.
 * Includes the legend label and bar color.
 *
 * @param {string} legendLabel - The label to display in the legend
 * @param {string} barColor - The color for the bar chart
 * @returns {ChartConfig} Chart configuration object
 *
 * @example
 * const config = createBarChartConfig("Total Sales MXN", "#EB6668");
 * <ChartContainer config={config}>
 */
export const createBarChartConfig = (legendLabel: string, barColor: string): ChartConfig => {
  return {
    value: {
      label: legendLabel,
      color: barColor,
    },
  };
};

/**
 * Creates a chart configuration for pie charts
 *
 * Builds the ChartConfig object required by shadcn ChartContainer for pie charts.
 * Maps each segment name to its corresponding color from the color variations array.
 *
 * @param {TransformedChartData[]} chartData - The transformed chart data with names and values
 * @param {string[]} colorVariations - Array of colors for pie segments
 * @returns {ChartConfig} Chart configuration object
 *
 * @example
 * const config = createPieChartConfig(chartData, ['#F04E4A', '#F26B67']);
 * <ChartContainer config={config}>
 */
export const createPieChartConfig = (chartData: TransformedChartData[], colorVariations: string[]): ChartConfig => {
  if (!chartData || chartData.length === 0) {
    return {};
  }

  return chartData.reduce((config, item, index) => {
    return {
      ...config,
      [item.name]: {
        label: item.name,
        color: colorVariations[index] || '#EB6668',
      },
    };
  }, {} as ChartConfig);
};

/**
 * Truncates text by width
 * @param text - Text to truncate
 * @param maxWidth - Maximum width in pixels
 * @param fontSize - Font size in pixels (default: 12)
 * @returns Truncated text
 *
 * @example
 * truncateByWidth('Hello World', 50)
 * // Returns: 'Hello…'
 *
 */
export const truncateLabelByWidth = (text: string, maxWidth: number, fontSize = 12): string => {
  const avgCharWidth = fontSize * 0.6;
  const maxChars = Math.floor(maxWidth / avgCharWidth);
  return text.length > maxChars ? `${text.slice(0, maxChars - 1)}…` : text;
};
